  import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { currentTenant } from '../config/tenant';
import storageService from './storageService';

class GoogleAuthService { 
  constructor() {
    this.initialized = false;
    this.initializing = false;
    this.initPromise = null;
    this.config = {
      webClientId: process.env.VUE_APP_GOOGLE_WEB_CLIENT_ID,
      androidClientId: process.env.VUE_APP_GOOGLE_ANDROID_CLIENT_ID,
      iosClientId: process.env.VUE_APP_GOOGLE_IOS_CLIENT_ID,
    };
  }

  getSocialLoginPlugin() {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('SocialLogin plugin is only available on native platforms');
    }
    

    
    // Try to get from Capacitor.Plugins
    const plugin = Capacitor.Plugins.SocialLogin;
    
    if (!plugin) {
      throw new Error('SocialLogin plugin not found in Capacitor.Plugins');
    }
    
    return plugin;
  }


  async initialize() {
    if (this.initialized) {
      console.log('Google Login already initialized');
      return;
    }

    // Jika sedang initializing, tunggu promise yang ada
    if (this.initializing && this.initPromise) {
      console.log('Google Login initialization in progress, waiting...');
      return this.initPromise;
    }

    // Mulai inisialisasi
    this.initializing = true;
    this.initPromise = this._doInitialize();

    try {
      await this.initPromise;
    } finally {
      this.initializing = false;
      this.initPromise = null;
    }
  }

  async _doInitialize() {
    try {
      const isNative = Capacitor.isNativePlatform();
      
      console.log('=== Google Login Initialization ===');
      console.log('Platform:', Capacitor.getPlatform());
      console.log('Is Native:', isNative);
      
      if (isNative) {
        const webClientId = this.config.webClientId;
        console.log('Web Client ID configured:', webClientId ? 'Yes' : 'No');
        console.log('Web Client ID:', webClientId);
        
        if (!webClientId) {
          throw new Error('Web Client ID is not configured. Please check VUE_APP_GOOGLE_WEB_CLIENT_ID in .env file');
        }
        
        // Initialize Google provider
        console.log('Initializing Google provider...');
        const SocialLogin = this.getSocialLoginPlugin();
        
        const iosClientId = this.config.iosClientId || process.env.VUE_APP_GOOGLE_IOS_CLIENT_ID;
        
        await SocialLogin.initialize({
          google: {
            webClientId: webClientId,
            iOSClientId: iosClientId || webClientId,
          }
        });
        
        console.log('✓ Google provider initialized successfully');
      } else {
        console.log('✓ Google Login (Web) - will use backend OAuth redirect');
      }

      this.initialized = true;
      console.log('=== Initialization Complete ===');
    } catch (error) {
      console.error('✗ Failed to initialize Google Login:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      this.initialized = false;
      throw new Error('Failed to initialize Google Login: ' + error.message);
    }
  }

  /**
   * Login dengan Google
   * @param {Object} [options]
   * @param {string} [options.ref] Base64 url redirect integrasi eksternal
   * @param {string} [options.merchantId] Merchant id dari URL. ref + merchantId terisi = akun baru dibuat
   *                                      langsung di backend (tanpa form) lalu login.
   * @returns {Promise<Object>} 
   */
  async login(options = {}) {
    try {
      if (!this.initialized) {
        await this.initialize();
      }

      const isNative = Capacitor.isNativePlatform();
      
      if (isNative) {
        const SocialLogin = this.getSocialLoginPlugin();
        
        console.log('Calling SocialLogin.login...');
        const response = await SocialLogin.login({
          provider: 'google',
          options: {
            scopes: ['email', 'profile']
          }
        });

        console.log('Login response:', response);

        if (response.provider !== 'google') {
          throw new Error('Invalid provider response');
        }

        const result = response.result;
        const platform = Capacitor.getPlatform();

        const backendResponse = await this.verifyWithBackend(result.idToken, platform, options);

        return backendResponse;
      } else {
        throw new Error('Web platform should use backend OAuth redirect. This method is for native only.');
      }

    } catch (error) {
      console.error('Google login failed:', error);

      // Auto-logout native Google session jika verifikasi backend gagal atau login error
      if (Capacitor.isNativePlatform()) {
        try {
          const SocialLogin = this.getSocialLoginPlugin();
          await SocialLogin.logout({
            provider: 'google'
          });
          console.log('[GoogleAuth] Native Google session cleared after failure');
        } catch (logoutError) {
          console.warn('[GoogleAuth] Non-blocking logout error after failure:', logoutError);
        }
      }

      throw error;
    }
  }

  /**
   * Verify Google token dengan backend
   * @param {string} idToken - Google ID Token
   * @param {string} platform - Platform (web, android, ios)
   * @returns {Promise<Object>} User data dan auth token
   */
  async verifyWithBackend(idToken, platform, options = {}) {
    try {
      let apiHost = (Capacitor.isNativePlatform() && currentTenant.apiHost)
        ? currentTenant.apiHost
        : (process.env.VUE_APP_API_HOST || currentTenant.apiHost || 'https://app.mymembership.id');
      
      apiHost = apiHost.replace(/\/+$/, '');
      const apiUrl = `${apiHost}/api/auth/google/verify-token`;
      
      // console.log('Verifying with backend:', apiUrl);
      // console.log('Platform:', platform);
      // console.log('Is Native:', Capacitor.isNativePlatform());
      // console.log('Tenant API Host:', currentTenant.apiHost);
      // console.log('Token length:', idToken.length);
      // console.log('Token preview:', idToken.substring(0, 50) + '...');

      const response = await axios.post(apiUrl, {
        idToken: idToken,
        platform: platform,
        ref: options.ref || null,
        merchant_id: options.merchantId || null,
        referral_code: localStorage.getItem('campaign_referral_code') || null
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Tenant-Domain': currentTenant.domain
        }
      });

      // console.log('Backend response:', response.data);

      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Verification failed');
      }

    } catch (error) {
      console.error('Backend verification failed details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      const errorCode = error.response?.data?.error || error.response?.data?.error_code || '';
      const displayMessage = error.response?.data?.message || errorCode || error.message;
      const authError = new Error(displayMessage);
      authError.code = errorCode || displayMessage; 
      authError.httpStatus = error.response?.status;
      authError.data = error.response?.data;
      throw authError;
    }
  }

  
  async logout() {
    try {
      if (Capacitor.isNativePlatform()) {
        const SocialLogin = this.getSocialLoginPlugin();
        try {
          await SocialLogin.logout({
            provider: 'google'
          });
        } catch (e) {
          // Sama seperti Apple: abaikan error logout native (sesi sudah tidak valid / belum terinisialisasi)
          // supaya clearAuthData() di bawah SELALU jalan dan auto-login dari native storage tidak hidup lagi.
        }
      }

      await storageService.clearAuthData();

      console.log('Logged out successfully');
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }

  /**
   * Check if user is logged in (check native storage untuk auto-login)
   * @returns {Promise<boolean>}
   */
  async isLoggedIn() {
    return await storageService.isLoggedIn();
  }

  /**
   * Get stored auth token (dari native storage)
   * @returns {Promise<string|null>}
   */
  async getAuthToken() {
    return await storageService.getItem('auth_token');
  }

  /**
   * Get stored user data (dari native storage)
   * @returns {Promise<Object|null>}
   */
  async getUserData() {
    return await storageService.getItemJSON('user_data');
  }

  /**
   * Sync auth data ke native storage (dipanggil setelah login berhasil)
   * @param {Object} data - Auth data (user, token)
   */
  async syncAuthData(data) {
    // Hanya sync ke native storage, jangan touch localStorage
    if (data.token) {
      await storageService.setItem('auth_token', data.token);
    }
    if (data.user) {
      await storageService.setItem('user_data', JSON.stringify(data.user));
    }
  }

  
  async clearAuthData() {
    await storageService.clearAuthData();
  }
}

export default new GoogleAuthService();